import { gql, gqlFetch } from '@/service/gql'
import { trackFieldsFrag } from '@/track/trackFieldsFrag'
import { artistFieldsFrag } from '@/artist/artistFieldsFrag'
import { albumFieldsFrag } from '@/album/albumFieldsFrag'
import type { SanitizedGql } from '@/util/gqlUtil'
import type { ResultOf, VariablesOf } from 'gql.tada'
import { getAuth } from '@/auth/authRef'
import { toRecommendationModel } from '@/browse/recommendationModel'
import { type Maybe, memoize } from '@amzn/vinyl-util'
import { notNull } from '@amzn/shiv'
import type { RecommendationResult, SearchOptions, SearchRecommender } from './SearchRecommender'
import { playlistFieldsFrag } from '@/playlistdetail/playlistFieldsFrag'
import { stationFieldsFrag } from '@/service/fragments/stationFieldsFrag'
import { podcastEpisodeFieldsFrag } from '@/podcast/podcastEpisodeFieldsFrag'
import { podcastShowFieldsFrag } from '@/podcast/podcastShowFieldsFrag'
import type { EntityUnion } from '@/service/fragments/entity'

// language=GRAPHQL
const searchQuery = gql(
    `query searchResults(
        $searchText: String!
        $specifications: [SearchSpecification]
        $isAuthenticated: Boolean!
    ) {
        specificationSearch(
            specificationSearchInput: { searchText: $searchText, specifications: $specifications }
        ) {
            id
            searchText
            result {
                __typename
                pageInfo {
                    hasNextPage
                    token
                }
                edgeCount
                totalCount
                label
                edges {
                    __typename
                    cursor
                    node {
                        ...albumFields
                        ...artistFields
                        ...playlistFields
                        ...trackFields
                        ...stationFields
                        ...podcastEpisodeFields
                        ...podcastShowFields
                    }
                }
            }
        }
    }`,
    [
        albumFieldsFrag,
        artistFieldsFrag,
        playlistFieldsFrag,
        trackFieldsFrag,
        stationFieldsFrag,
        podcastEpisodeFieldsFrag,
        podcastShowFieldsFrag,
    ]
)

export type SearchResult = SanitizedGql<ResultOf<typeof searchQuery>>
export type SearchSpecification = NonNullable<SearchVariables['specifications']>[number]
export type SearchVariables = VariablesOf<typeof searchQuery>

const TOP_RESULTS_LIMIT = 10
const HORIZONTAL_ITEM_LIMIT = 12
const VERTICAL_ITEM_LIMIT = 10

export const defaultSearchSpecifications = [
    {
        label: 'TopResults',
        types: [
            'album',
            'artist',
            'playlist',
            'track',
            'station',
            'podcast_show',
            'podcast_episode',
        ],
        limit: TOP_RESULTS_LIMIT,
    },
    { label: 'Albums', types: ['album'], limit: VERTICAL_ITEM_LIMIT },
    { label: 'Artists', types: ['artist'], limit: VERTICAL_ITEM_LIMIT },
    { label: 'Playlists', types: ['playlist'], limit: VERTICAL_ITEM_LIMIT },
    // TODO: https://gitlab.aws.dev/amazonmusic/musicfirefly/amu_webapi/-/blob/main/src/models/Inputs/SpecificationSearchInput.ts#L13 why doesn't this work???
    // { label: 'CommunityPlaylists', types: ['community_playlist'], limit: VERTICAL_ITEM_LIMIT },
    { label: 'Songs', types: ['track'], limit: HORIZONTAL_ITEM_LIMIT },
    { label: 'Stations', types: ['station'], limit: VERTICAL_ITEM_LIMIT },
    {
        label: 'Podcasts',
        types: ['podcast_show'],
        limit: HORIZONTAL_ITEM_LIMIT,
    },
    {
        label: 'PodcastEpisodes',
        types: ['podcast_episode'],
        limit: HORIZONTAL_ITEM_LIMIT,
    },
] as const satisfies SearchSpecification[]

export async function fetchSearch(
    options: SearchOptions,
    specifications: SearchSpecification[]
): Promise<SearchResult> {
    return gqlFetch(searchQuery, {
        searchText: options.query,
        isAuthenticated: getAuth().isSignedIn(),
        specifications,
    })
}

/**
 * Creates a memoized search recommender for the given search specifications
 */
export function createSearchRecommender(
    specifications: SearchSpecification[] = defaultSearchSpecifications
): SearchRecommender {
    return memoize(
        (options: SearchOptions): Promise<readonly RecommendationResult[]> =>
            fetchSearch(options, specifications).then((result: SearchResult) => {
                const results = result.specificationSearch?.result ?? []
                return results
                    .map((result) => {
                        return {
                            label: result.label ?? '',
                            items: edgesToRecommendationModels(result.edges),
                        }
                    })
                    .filter((e) => e.items.length)
            }),
        (options) => options.query
    )
}

function edgesToRecommendationModels(
    edges: Maybe<
        ReadonlyArray<{
            readonly node: EntityUnion
        }>
    >
) {
    return (
        edges
            ?.map((edge) => {
                return toRecommendationModel(edge.node)
            })
            .filter(notNull) ?? []
    )
}
